import {useEffect, useRef} from 'react';

import {isNotNil} from 'ramda';

import type {EagColumnState} from '../types/EagColumnState';
import type {Filters} from '../types/Filters';
import type {GridSortState, GridStateChange} from '../types/GridStateChange';
import {unwrapFilterValuesRaw} from '../utils/unwrapFilterValuesRaw';

type UseGridStateSyncParams = {
  filterValues: Filters;
  columnStates: EagColumnState[];
  onGridStateChange: ((state: GridStateChange) => void) | undefined;
};

const toSortState = (columnStates: EagColumnState[]): GridSortState =>
  columnStates
    .filter((columnState) => isNotNil(columnState.sort))
    .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
    .map((columnState) => ({
      columnKey: columnState.key,
      direction: columnState.sort === 'ASC' ? 'asc' : 'desc',
    }));

/**
 * Notifies the host of the grid's filters + sort on initial load and on every change (mirrors
 * useGridFilterSync but also tracks sort and fires on mount). Sort is value-compared so a
 * columnStates rebuild from an unrelated change (resize/pin) doesn't trigger a spurious notification.
 */
export const useGridStateSync = (params: UseGridStateSyncParams): void => {
  const onGridStateChangeRef = useRef(params.onGridStateChange);

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- syncs the callback ref every render (avoids stale closure)
  useEffect(function syncOnGridStateChangeRef() {
    onGridStateChangeRef.current = params.onGridStateChange;
  });

  const sortState = toSortState(params.columnStates);
  const sortStateKey = JSON.stringify(sortState);
  const prevFilterValuesRef = useRef<Filters | null>(null);
  const prevSortStateKeyRef = useRef<string | null>(null);

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- reactive notification on mount + filter/sort change
  useEffect(
    function notifyGridStateChange() {
      const isInitialNotification = prevFilterValuesRef.current === null;
      const haveFiltersChanged = prevFilterValuesRef.current !== params.filterValues;
      const hasSortChanged = prevSortStateKeyRef.current !== sortStateKey;

      if (!isInitialNotification && !haveFiltersChanged && !hasSortChanged) {
        return;
      }

      prevFilterValuesRef.current = params.filterValues;
      prevSortStateKeyRef.current = sortStateKey;

      onGridStateChangeRef.current?.({
        filters: unwrapFilterValuesRaw(params.filterValues),
        sort: sortState,
      });
    },
    [params.filterValues, sortStateKey] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );
};
